import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Scale, Loader2, Radar, ListPlus, Check, X, PlugZap, CalendarCheck, History, Undo2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, PageState, Empty, UpgradeGate } from "@/components/app/Common";
import { NOT_CONFIGURED, ConfidenceBadge, Sources } from "@/components/app/Research";
import { DeadlineUpdateDialog } from "@/components/app/DeadlineUpdate";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const MAT = { high: "bg-red-500/12 text-red-600 dark:text-red-400", medium: "bg-amber-500/12 text-amber-600 dark:text-amber-400", low: "bg-slate-500/12 text-slate-500" };
const CAT = { tax: "Tax", filing_deadline: "Filing deadline", compliance: "Compliance", permit: "Permit / licence", industry: "Industry", employment: "Employment", other: "Other" };
const STATUS = { task: "Task created", acknowledged: "Reviewed", applied: "Calendar updated" };

const useAct = (reload) => {
  const [busy, setBusy] = useState("");
  const act = async (k, fn, msg) => {
    setBusy(k);
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  return [busy, act];
};

const Actions = ({ a, reload, onDeadline }) => {
  const [busy, act] = useAct(reload);
  const low = a.confidence?.level === "low";
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {a.deadline_ready && <Button size="sm" onClick={() => onDeadline(a.id)} data-testid={`reg-deadline-${a.id}`}><CalendarCheck className="h-3.5 w-3.5 mr-1" />Review calendar update</Button>}
      {!low && <Button size="sm" variant={a.deadline_ready ? "outline" : "default"} onClick={() => act("task", () => api.post(`/regwatch/alerts/${a.id}/task`), "Task created")} disabled={!!busy} data-testid={`reg-task-${a.id}`}>{busy === "task" ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ListPlus className="h-3.5 w-3.5 mr-1" />}Create task</Button>}
      <Button size="sm" variant="outline" onClick={() => act("ack", () => api.post(`/regwatch/alerts/${a.id}/status`, { status: "acknowledged" }), "Marked as reviewed")} disabled={!!busy} data-testid={`reg-ack-${a.id}`}><Check className="h-3.5 w-3.5 mr-1" />Reviewed</Button>
      <Button size="sm" variant="ghost" onClick={() => act("dis", () => api.post(`/regwatch/alerts/${a.id}/status`, { status: "dismissed" }), "Dismissed")} disabled={!!busy} data-testid={`reg-dismiss-${a.id}`}><X className="h-3.5 w-3.5 mr-1" />Not relevant</Button>
    </div>
  );
};

const Alert = ({ a, reload, onDeadline }) => (
  <div className="py-4" data-testid={`reg-alert-${a.id}`}>
    <div className="flex flex-wrap items-center gap-2"><b className="text-sm">{a.title}</b>
      <ConfidenceBadge c={a.confidence} testId={`reg-confidence-${a.id}`} />
      <span className={`text-[11px] rounded-full px-2 py-0.5 ${MAT[a.materiality]}`}>{a.materiality} materiality</span>
      <span className="text-[11px] text-muted-foreground">{CAT[a.category]} · {a.jurisdiction}{a.effective_date && ` · effective ${fmtDate(a.effective_date)}`}</span>
      {STATUS[a.status] && <span className="text-[11px] rounded-full px-2 py-0.5 bg-primary/10 text-primary" data-testid={`reg-status-${a.id}`}>{STATUS[a.status]}</span>}</div>
    {a.confidence?.level === "low" && <p className="text-xs mt-1 inline-flex items-center gap-1 text-muted-foreground" data-testid={`reg-info-only-${a.id}`}><Info className="h-3.5 w-3.5" />Informational only — low-confidence findings never trigger actions.</p>}
    <p className="text-sm mt-1">{a.what_changed}</p>
    {a.impact && <p className="text-xs text-muted-foreground mt-1"><b>Why it matters:</b> {a.impact}{a.affected?.length > 0 && ` (${a.affected.join(", ")})`}</p>}
    {a.recommended_action && <p className="text-xs mt-1"><b>Recommended:</b> {a.recommended_action}</p>}
    {a.deadline && <p className="text-xs mt-1" data-testid={`reg-deadline-info-${a.id}`}><b>Deadline change:</b> {a.deadline.applies_to} → {fmtDate(a.deadline.new_due_date)} (AI-suggested){!a.deadline_ready && " · calendar updates need high confidence and an official government source"}</p>}
    <Sources sources={a.sources} testId={`reg-sources-${a.id}`} />
    {a.status === "new" && <Actions a={a} reload={reload} onDeadline={onDeadline} />}
  </div>
);

const SettingsCard = ({ d, reload }) => {
  const [f, setF] = useState({ enabled: true, industries: "", extra: "" });
  useEffect(() => {
    setF({ enabled: d.profile.enabled !== false, industries: d.profile.industries || "", extra: (d.profile.extra_locations || []).join(", ") });
  }, [d]);
  const save = async () => {
    try {
      await api.put("/regwatch/settings", { enabled: f.enabled, industries: f.industries, extra_locations: f.extra.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Watch settings saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="surface p-5 space-y-3" data-testid="reg-settings">
      <div className="flex items-center justify-between"><b className="text-sm">Weekly check</b><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="reg-enabled-switch" /></div>
      <p className="text-xs text-muted-foreground">Runs every Sunday so material changes at or above <b>{d.min_confidence}</b> confidence reach Monday's Attention Board and briefing.</p>
      <div className="text-xs"><b>Watching:</b> {d.locations.join("; ") || "add an entity to start"}</div>
      <Input placeholder="Industries, e.g. restaurants, trucking, dental clinics" value={f.industries} onChange={(e) => setF({ ...f, industries: e.target.value })} data-testid="reg-industries-input" />
      <Input placeholder="Extra places you operate (comma separated), e.g. Quebec, Ohio" value={f.extra} onChange={(e) => setF({ ...f, extra: e.target.value })} data-testid="reg-extra-input" />
      <Button size="sm" variant="outline" onClick={save} data-testid="reg-settings-save">Save</Button>
    </div>
  );
};

const Status = ({ d }) => (
  <div className="surface p-5" data-testid="reg-connection">
    <b className="text-sm">Research status</b>
    {!d.configured ? <p className="text-xs text-muted-foreground mt-2 flex gap-1.5" data-testid="reg-not-configured"><PlugZap className="h-3.5 w-3.5 shrink-0" />{NOT_CONFIGURED}</p>
      : <p className="text-xs mt-2" data-testid="reg-feature-state">{d.enabled ? "Regulatory Watch is on." : "Connected, but Regulatory Watch is off until you validate it."}</p>}
    <Link to="/app/research-lab" className="text-xs text-primary hover:underline mt-2 inline-block" data-testid="reg-lab-link">Open Research validation →</Link>
  </div>
);

const Audit = () => {
  const state = useLoad(() => api.get("/audit").then((r) => r.data));
  const [busy, act] = useAct(state.reload);
  const rows = state.data || [];
  return (
    <div className="surface p-5" data-testid="reg-audit">
      <div className="flex items-center gap-2"><History className="h-4 w-4 text-primary" /><b className="text-sm">Audit trail</b></div>
      {!rows.length ? <p className="text-xs text-muted-foreground mt-2">No compliance records have been changed from external research.</p>
        : <ul className="mt-2 divide-y divide-border text-xs">{rows.map((e) => (
          <li key={e.id} className="py-2" data-testid={`audit-row-${e.id}`}>
            <div><b>{e.title}</b> · {e.entity_name}: <span className="font-mono">{fmtDate(e.old_value)} → {fmtDate(e.new_value)}</span>{e.action === "deadline_revert" && " (revert)"}</div>
            <div className="text-muted-foreground">{fmtDate(e.at)} by {e.actor_email} · {e.reason}{e.confidence && ` · ${e.confidence.level} confidence (${e.confidence.score})`}{e.note && ` · note: ${e.note}`}{e.entered_by_owner && ` · date typed by owner${e.suggested_value && e.suggested_value !== e.new_value ? ` (AI suggested ${e.suggested_value})` : ""}`}</div>
            {e.verified_source_url && <div className="text-muted-foreground">Verified against: <a href={e.verified_source_url} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline break-all">{e.verified_source_url}</a></div>}
            {e.sources?.length > 0 && <div className="text-muted-foreground">Sources: {e.sources.map((s, i) => <a key={s.url} href={s.url} target="_blank" rel="noreferrer noopener" className="text-primary hover:underline mr-1">[{i + 1}]</a>)}</div>}
            {e.action === "deadline_update" && (e.reverted_at ? <span className="text-muted-foreground">Reverted {fmtDate(e.reverted_at)}</span>
              : <Button size="sm" variant="ghost" className="h-7 px-2 mt-1" disabled={!!busy} onClick={() => window.confirm(`Revert ${e.title} to ${e.old_value}?`) && act(e.id, () => api.post(`/audit/${e.id}/revert`, { confirm: true }), "Deadline reverted")} data-testid={`audit-revert-${e.id}`}><Undo2 className="h-3 w-3 mr-1" />Revert</Button>)}
          </li>))}</ul>}
    </div>
  );
};

export default function RegulatoryWatch() {
  const state = useLoad(() => api.get("/regwatch").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(null);
  const [auditKey, setAuditKey] = useState(0);
  if (state.gated) return <div><PageHeader eyebrow="External intelligence" title="Regulatory Watch" /><UpgradeGate gated={state.gated} /></div>;
  const d = state.data;
  const run = async () => {
    setBusy(true);
    try {
      const r = (await api.post("/regwatch/run")).data;
      toast.success(r.new_alerts ? `${r.new_alerts} new change(s) found` : "No new changes found");
      state.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div>
      <PageHeader eyebrow="External intelligence" title="Regulatory Watch" subtitle="Weekly web check for changes to tax rules, filing deadlines, compliance, licensing and industry regulations where your companies operate — scored for source confidence, with recommended actions.">
        <Button onClick={run} disabled={busy || !d?.configured || !d?.enabled || !d?.locations?.length} data-testid="reg-run-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Radar className="h-4 w-4 mr-1.5" />}Check now</Button>
      </PageHeader>
      <PageState state={state}>
        {d && <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6 order-2 lg:order-1">
            <div className="surface p-6">
              {d.last_run && <p className="text-xs text-muted-foreground mb-2" data-testid="reg-last-run">Last checked {fmtDate(d.last_run.at)} · {d.last_run.new_alerts} new{d.last_run.summary && ` · ${d.last_run.summary}`}</p>}
              {d.alerts.length ? <div className="divide-y divide-border">{d.alerts.map((a) => <Alert key={a.id} a={a} reload={state.reload} onDeadline={setDl} />)}</div>
                : <Empty icon={Scale} title="No regulatory changes yet" text={d.configured ? "Run a check now, or wait for Sunday's automatic scan." : NOT_CONFIGURED} testId="reg-empty" />}
            </div>
            <Audit key={auditKey} />
          </div>
          <div className="space-y-4 order-1 lg:order-2"><Status d={d} /><SettingsCard d={d} reload={state.reload} /></div>
        </div>}
      </PageState>
      {dl && <DeadlineUpdateDialog alertId={dl} onClose={() => setDl(null)} done={() => { state.reload(); setAuditKey((k) => k + 1); }} />}
    </div>
  );
}
